/**
 * RoadmapPrimitivesLabPage — /design-lab/primitives
 *
 * Internal Phase 5a review surface. Renders each Stage A primitive in all
 * its states so reviewers can verify visual + interaction behavior before
 * Stage B composes them into TaskCard / WeekRail / PhaseSection.
 *
 * Delete after Phase 5 ships, or keep as a permanent dev surface for
 * future primitive additions. Not linked from any user-facing page.
 */

import { useMemo, useState } from "react";
import { Box, Container, Divider, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Trans, useTranslation } from "react-i18next";

import TaskCheckbox from "../components/roadmap/TaskCheckbox";
import ProductChip from "../components/roadmap/ProductChip";
import StatusDot from "../components/roadmap/StatusDot";
import ProgressBar from "../components/roadmap/ProgressBar";
import TaskCard from "../components/roadmap/TaskCard";
import WeekRail from "../components/roadmap/WeekRail";
import PhaseSection from "../components/roadmap/PhaseSection";
import RoadmapIntro from "../components/roadmap/RoadmapIntro";
import RoadmapMinimap from "../components/roadmap/RoadmapMinimap";
import RoadmapStickyHeader from "../components/roadmap/RoadmapStickyHeader";
import type {
  RoadmapPhase,
  RoadmapResponse,
  RoadmapTask,
  RoadmapWeek,
} from "../api/client";

// ---------------------------------------------------------------------------
// Fixture data — lives in this lab page only. Real data comes from the API.
// ---------------------------------------------------------------------------

function makeTask(overrides: Partial<RoadmapTask> & Pick<RoadmapTask, "id" | "title">): RoadmapTask {
  return {
    summary: "",
    checklist: [],
    estimate_hours: 0,
    due_day: null,
    routes: [],
    primary_cta: null,
    is_focus: false,
    blocked: false,
    done: false,
    ...overrides,
  };
}

const FIXTURE_TASKS_WEEK_2: RoadmapTask[] = [
  makeTask({
    id: "p1w2t1",
    title: "Collect 50 hand-labeled eval examples across 3 failure modes",
    summary:
      "Build the bedrock dataset for any RAG eval harness — diverse failure cases beat clean wins.",
    checklist: [
      "Pull 100 production-ish queries from your domain corpus",
      "Hand-label 50 with the 3 failure modes you most fear",
      "Save as JSONL with input, expected, mode columns",
    ],
    estimate_hours: 6,
    due_day: "Wed",
    routes: [
      {
        product_slug: "agentic-mastery",
        label: "Agentic Mastery · M4",
        href: "/agentic-ai-mastery/module/eval-harness",
      },
    ],
    is_focus: true,
  }),
  makeTask({
    id: "p1w2t2",
    title: "Ship RAG v1 + write 1-page public post-mortem",
    summary:
      "Prove you can ship and reflect — the post-mortem is half the artifact, recruiters look for it.",
    checklist: ["Deploy to a free-tier host", "Write the post-mortem", "Publish on LinkedIn + GitHub"],
    estimate_hours: 8,
    due_day: "Fri",
    routes: [
      {
        product_slug: "portfolio-kit",
        label: "Portfolio Kit · RAG",
        href: "/portfolio-kit/archetype/rag-agent",
      },
      {
        product_slug: "linkedin-presence",
        label: "LinkedIn · Post",
        href: "/linkedin-presence/content",
      },
    ],
  }),
];

const FIXTURE_TASKS_WEEK_1: RoadmapTask[] = [
  makeTask({
    id: "p1w1t1",
    title: "Set up evaluation environment + version-controlled prompt repo",
    summary:
      "Foundation for every later week — eval discipline you build now scales across every product.",
    checklist: ["Create eval harness repo", "Wire to your model API", "Commit baseline prompts"],
    estimate_hours: 4,
    routes: [
      {
        product_slug: "agentic-mastery",
        label: "Agentic Mastery · M3",
        href: "/agentic-ai-mastery/module/setup",
      },
    ],
    done: true,
  }),
  makeTask({
    id: "p1w1t2",
    title: "Read 3 production agent post-mortems + write reactions",
    summary:
      "Pattern recognition for failure modes you'll personally hit in weeks 2–4.",
    estimate_hours: 3,
    done: true,
  }),
];

const FIXTURE_TASKS_WEEK_3: RoadmapTask[] = [
  makeTask({
    id: "p1w3t1",
    title: "Add observability: trace every LLM call with cost + latency",
    summary:
      "Without traces, debugging is guesswork — instrument before adding the second tool call.",
    estimate_hours: 5,
    routes: [
      {
        product_slug: "agentic-mastery",
        label: "Agentic Mastery · M5",
        href: "/agentic-ai-mastery/module/tracing",
      },
    ],
    blocked: true, // demo: intentionally blocked to show disabled checkbox UX
  }),
];

const FIXTURE_PHASE: RoadmapPhase = {
  index: 1,
  slug: "foundation",
  title: "Foundation",
  week_range: [1, 3],
  status: "current",
  summary:
    "Lay the eval discipline + first shipped artifact that every later phase routes back to. Finishing this phase means a public RAG v1 with measurable reliability.",
  weeks: [
    {
      index: 1,
      title: "Tooling + reading",
      status: "done",
      tasks: FIXTURE_TASKS_WEEK_1,
    },
    {
      index: 2,
      title: "Ship RAG v1 + post-mortem",
      status: "current",
      tasks: FIXTURE_TASKS_WEEK_2,
    },
    {
      index: 3,
      title: "Observability + iteration",
      status: "upcoming",
      tasks: FIXTURE_TASKS_WEEK_3,
    },
  ],
};

const FIXTURE_PHASE_COMPACT: RoadmapPhase = {
  index: 4,
  slug: "interview",
  title: "Interview",
  week_range: [10, 12],
  status: "upcoming",
  summary:
    "Convert reps into offers. Finishing this phase means signed offer letter from a target company.",
  weeks: [
    {
      index: 10,
      title: "Mock interview gauntlet",
      status: "upcoming",
      tasks: [
        makeTask({ id: "p4w10t1", title: "5 mocks against company-specific principles" }),
        makeTask({ id: "p4w10t2", title: "Daily STAR drills, score trend tracking" }),
      ],
    },
    {
      index: 11,
      title: "Onsite simulation week",
      status: "upcoming",
      tasks: [makeTask({ id: "p4w11t1", title: "Full-day sim: behavioral + system design" })],
    },
    {
      index: 12,
      title: "Negotiation + offer landing",
      status: "upcoming",
      tasks: [
        makeTask({ id: "p4w12t1", title: "Run the 5 negotiation moves" }),
        makeTask({ id: "p4w12t2", title: "Sign + onboard checklist" }),
      ],
    },
  ],
};

const FIXTURE_WEEK_STANDALONE: RoadmapWeek = FIXTURE_PHASE.weeks[1];

// A realistic four-phase fixture for the Stage C composites that need a full
// roadmap. Phases 2 and 3 are sketched lightly — just titles and weeks — to
// give RoadmapMinimap something meaningful to render.
const FIXTURE_PHASE_2: RoadmapPhase = {
  index: 2,
  slug: "evidence",
  title: "Evidence",
  week_range: [4, 6],
  status: "upcoming",
  summary:
    "Stack two more shipped projects that route into your portfolio + GitHub. Finishing this phase means three production-grade artifacts a hiring manager can verify.",
  weeks: [
    { index: 4, title: "Multi-agent researcher v1", status: "upcoming", tasks: [makeTask({ id: "p2w4t1", title: "Scaffold multi-agent loop" }), makeTask({ id: "p2w4t2", title: "First successful 3-agent run" })] },
    { index: 5, title: "HITL workflow agent", status: "upcoming", tasks: [makeTask({ id: "p2w5t1", title: "Wire approval gates" })] },
    { index: 6, title: "Portfolio site v1", status: "upcoming", tasks: [makeTask({ id: "p2w6t1", title: "Lovable + Supabase build" }), makeTask({ id: "p2w6t2", title: "Deploy + custom domain" })] },
  ],
};

const FIXTURE_PHASE_3: RoadmapPhase = {
  index: 3,
  slug: "visibility",
  title: "Visibility",
  week_range: [7, 9],
  status: "upcoming",
  summary:
    "Build the recruiter-facing surface so the artifacts get seen. Finishing this phase means an inbound conversation pipeline running on its own.",
  weeks: [
    { index: 7, title: "LinkedIn presence rebuild", status: "upcoming", tasks: [makeTask({ id: "p3w7t1", title: "Profile + first 5 posts" }), makeTask({ id: "p3w7t2", title: "Outreach to 10 ICs" })] },
    { index: 8, title: "Resume + ATS score pass", status: "upcoming", tasks: [makeTask({ id: "p3w8t1", title: "Run ATS scorer + iterate" })] },
    { index: 9, title: "Application + referral wave", status: "upcoming", tasks: [makeTask({ id: "p3w9t1", title: "Apply to 15 targeted roles" }), makeTask({ id: "p3w9t2", title: "Activate 3 referrals" })] },
  ],
};

const FIXTURE_ROADMAP: RoadmapResponse = {
  id: "fixture-roadmap-id",
  created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
  started_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
  target_at: new Date(Date.now() + 70 * 24 * 60 * 60 * 1000).toISOString(),
  last_revised_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  archetype: {
    slug: "swe-to-ai-standard",
    name: "Standard Build",
    confidence: "High",
    hypothesis:
      "You're a strong backend engineer with shipped agentic-AI side projects but no production AI/ML role yet — a personalized build is right.",
  },
  overall: { score: 64, tier: "building", weeks_to_ready: 12, pace: "Standard Build" },
  target_audience: "swe_to_ai",
  dimensions: [
    {
      slug: "technical",
      title: "Technical",
      subtitle: "Agentic AI architecture + eval discipline",
      score: 78,
      tier: "strong",
      reading: "You're already shipping agents and reasoning carefully about failure modes.",
      emphasis: "Use this strength to anchor portfolio projects.",
      signals: [],
    },
    {
      slug: "portfolio",
      title: "Portfolio",
      subtitle: "Public, verifiable artifacts",
      score: 38,
      tier: "gap",
      reading: "Your code lives in private repos and one-off side projects.",
      emphasis: "Shipped + post-mortem'd projects.",
      signals: [],
    },
    {
      slug: "narrative",
      title: "Narrative",
      subtitle: "Cross-domain story",
      score: 45,
      tier: "gap",
      reading: "You don't yet have a 60-second story for the pivot.",
      emphasis: "Frame the bridge from backend to AI/ML explicitly.",
      signals: [],
    },
    {
      slug: "network",
      title: "Network",
      subtitle: "AI/ML practitioner reach",
      score: 55,
      tier: "building",
      reading: "Some second-degree connections, no warm intros yet.",
      emphasis: "Activate via shipped artifacts + LinkedIn presence.",
      signals: [],
    },
    {
      slug: "interview",
      title: "Interview",
      subtitle: "AI/ML behavioral + system design",
      score: 60,
      tier: "building",
      reading: "Strong behavioral baseline; system design needs reps.",
      emphasis: "5 mock interviews against AI/ML system design prompts.",
      signals: [],
    },
  ],
  means_it_does: [],
  means_it_does_not: [],
  primary_risk: "",
  key_insight: "",
  phases: [FIXTURE_PHASE, FIXTURE_PHASE_2, FIXTURE_PHASE_3, FIXTURE_PHASE_COMPACT],
  compensation_guidance: "",
  financial_bridge_recommendation: "",
  internal_vs_external_recommendation: "",
  milestones_to_watch: [],
  honest_limitations: "",
  usage: {
    tier: "all_access",
    generations_used: 1,
    limit_display: "Unlimited",
    can_generate_again: true,
  },
  estimate: {
    recommended_track: "portfolio",
    track_rationale:
      "Portfolio fits a ~6-month goal at this pace — and it's the credential the market rewards.",
    knowledge: {
      scoped_hours: [168, 200],
      weeks: [16, 25],
      ladder: [
        { hours_band: "5-10", weeks_low: 24, weeks_high: 44 },
        { hours_band: "10-15", weeks_low: 16, weeks_high: 25 },
        { hours_band: "15-20", weeks_low: 12, weeks_high: 17 },
        { hours_band: "20-30", weeks_low: 8, weeks_high: 13 },
        { hours_band: "40+", weeks_low: 5, weeks_high: 7 },
      ],
      distinction_uplift_hours: [0, 0],
    },
    portfolio: {
      scoped_hours: [234, 320],
      weeks: [24, 40],
      ladder: [
        { hours_band: "5-10", weeks_low: 35, weeks_high: 71 },
        { hours_band: "10-15", weeks_low: 24, weeks_high: 40 },
        { hours_band: "15-20", weeks_low: 18, weeks_high: 28 },
        { hours_band: "20-30", weeks_low: 11, weeks_high: 21 },
        { hours_band: "40+", weeks_low: 7, weeks_high: 11 },
      ],
      distinction_uplift_hours: [15, 25],
    },
    experience_tier: "Proficient",
    experience_multiplier: 1.0,
    effective_weekly: [9, 12],
    calibration_note:
      "*estimate based on typical learners; refined as our first cohort completes the program.",
  },
  assessment: {
    q4_skill_matrix: { d1: 3, d2: 2, d3: 3, d4: 1, d5: 3, d6: 1, d7: 2 },
    q5_target_role: "agentic_eng",
    q6_target_seniority: "mid_senior",
  },
};

function Section({ title, anchor, children }: { title: string; anchor: string; children: React.ReactNode }) {
  return (
    <Box component="section" id={anchor} sx={{ scrollMarginTop: "16px" }}>
      <Typography variant="h5" component="h2" sx={{ fontWeight: 500, mb: 1 }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 3 }} />
      {children}
    </Box>
  );
}

function mergeDoneState(phase: RoadmapPhase, doneIds: Set<string>): RoadmapPhase {
  return {
    ...phase,
    weeks: phase.weeks.map((w) => mergeWeek(w, doneIds)),
  };
}

function mergeWeek(week: RoadmapWeek, doneIds: Set<string>): RoadmapWeek {
  return {
    ...week,
    tasks: week.tasks.map((t) => ({ ...t, done: doneIds.has(t.id) })),
  };
}

function findFocusTaskId(phase: RoadmapPhase): string | undefined {
  for (const w of phase.weeks) {
    for (const t of w.tasks) {
      if (t.is_focus) return t.id;
    }
  }
  return undefined;
}

function Cell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack spacing={1.5} alignItems="center" sx={{ minWidth: 120 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
        {label}
      </Typography>
      <Box sx={{ minHeight: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {children}
      </Box>
    </Stack>
  );
}

export default function RoadmapPrimitivesLabPage() {
  const theme = useTheme();
  const { t } = useTranslation("pages-account");
  const [demoUnchecked, setDemoUnchecked] = useState(false);
  const [demoChecked, setDemoChecked] = useState(true);
  const [progressValue, setProgressValue] = useState(7);

  // Toggle state for Stage B demos — shared across all the fixture surfaces
  // so toggling a task in TaskCard, WeekRail, or PhaseSection propagates.
  const [doneTaskIds, setDoneTaskIds] = useState<Set<string>>(
    () => new Set(["p1w1t1", "p1w1t2"]), // matches the fixture's `done: true` initial state
  );

  const handleToggle = (taskId: string, next: boolean) => {
    setDoneTaskIds((prev) => {
      const updated = new Set(prev);
      if (next) updated.add(taskId);
      else updated.delete(taskId);
      return updated;
    });
  };

  // Merge fixtures with current toggle state — same pattern Stage D will use
  // in the real RoadmapPage orchestrator (since `task.done` from the API is
  // always false; truth lives in the completions endpoint).
  const phaseWithToggleState = useMemo(
    () => mergeDoneState(FIXTURE_PHASE, doneTaskIds),
    [doneTaskIds],
  );
  const weekWithToggleState = useMemo(
    () => mergeWeek(FIXTURE_WEEK_STANDALONE, doneTaskIds),
    [doneTaskIds],
  );
  const focusTaskId = useMemo(
    () => findFocusTaskId(phaseWithToggleState),
    [phaseWithToggleState],
  );

  // Stage C — full roadmap with toggle state merged in. Used by RoadmapIntro,
  // RoadmapMinimap, RoadmapStickyHeader.
  const roadmapWithToggleState = useMemo<RoadmapResponse>(
    () => ({
      ...FIXTURE_ROADMAP,
      phases: FIXTURE_ROADMAP.phases.map((p) => mergeDoneState(p, doneTaskIds)),
    }),
    [doneTaskIds],
  );

  const allTasks = roadmapWithToggleState.phases.flatMap((p) =>
    p.weeks.flatMap((w) => w.tasks ?? []),
  );
  const progress = {
    done: allTasks.filter((task) => task.done).length,
    total: allTasks.length,
    pct: allTasks.length > 0
      ? Math.round((allTasks.filter((task) => task.done).length / allTasks.length) * 100)
      : 0,
  };

  const currentPhase =
    roadmapWithToggleState.phases.find((p) => p.status === "current") ?? null;
  const currentWeek =
    currentPhase?.weeks.find((w) => w.status === "current") ?? null;
  const focusTask = useMemo(() => {
    for (const p of roadmapWithToggleState.phases) {
      for (const w of p.weeks) {
        for (const task of w.tasks ?? []) {
          if (task.is_focus) return task;
        }
      }
    }
    return null;
  }, [roadmapWithToggleState]);

  const nextMilestone = currentPhase
    ? {
        label: t("RoadmapPrimitivesLabPage.finishMilestoneLabel", { title: currentPhase.title }),
        weekIndex: currentPhase.week_range[1],
      }
    : null;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <Stack spacing={1} sx={{ mb: 5 }}>
        <Typography
          variant="overline"
          color="secondary.main"
          sx={{ fontWeight: 600, letterSpacing: "0.08em" }}
        >
          {t("RoadmapPrimitivesLabPage.eyebrow")}
        </Typography>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 500 }}>
          {t("RoadmapPrimitivesLabPage.heading")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
          {t("RoadmapPrimitivesLabPage.subheading")}
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ mt: 2, flexWrap: "wrap" }} useFlexGap>
          <a href="#checkbox" style={{ color: "inherit", fontSize: 13 }}>{t("RoadmapPrimitivesLabPage.navTaskCheckbox")}</a>
          <span style={{ color: theme.palette.divider }}>·</span>
          <a href="#chip" style={{ color: "inherit", fontSize: 13 }}>{t("RoadmapPrimitivesLabPage.navProductChip")}</a>
          <span style={{ color: theme.palette.divider }}>·</span>
          <a href="#dot" style={{ color: "inherit", fontSize: 13 }}>{t("RoadmapPrimitivesLabPage.navStatusDot")}</a>
          <span style={{ color: theme.palette.divider }}>·</span>
          <a href="#bar" style={{ color: "inherit", fontSize: 13 }}>{t("RoadmapPrimitivesLabPage.navProgressBar")}</a>
          <span style={{ color: theme.palette.divider }}>·</span>
          <a href="#taskcard" style={{ color: "inherit", fontSize: 13 }}>{t("RoadmapPrimitivesLabPage.navTaskCard")}</a>
          <span style={{ color: theme.palette.divider }}>·</span>
          <a href="#weekrail" style={{ color: "inherit", fontSize: 13 }}>{t("RoadmapPrimitivesLabPage.navWeekRail")}</a>
          <span style={{ color: theme.palette.divider }}>·</span>
          <a href="#phasesection" style={{ color: "inherit", fontSize: 13 }}>{t("RoadmapPrimitivesLabPage.navPhaseSection")}</a>
          <span style={{ color: theme.palette.divider }}>·</span>
          <a href="#intro" style={{ color: "inherit", fontSize: 13 }}>{t("RoadmapPrimitivesLabPage.navRoadmapIntro")}</a>
          <span style={{ color: theme.palette.divider }}>·</span>
          <a href="#minimap" style={{ color: "inherit", fontSize: 13 }}>{t("RoadmapPrimitivesLabPage.navRoadmapMinimap")}</a>
          <span style={{ color: theme.palette.divider }}>·</span>
          <a href="#stickyheader" style={{ color: "inherit", fontSize: 13 }}>{t("RoadmapPrimitivesLabPage.navRoadmapStickyHeader")}</a>
        </Stack>
      </Stack>

      <Stack spacing={6}>
        {/* ── TaskCheckbox ──────────────────────────────────────────────── */}
        <Section title={t("RoadmapPrimitivesLabPage.navTaskCheckbox")} anchor="checkbox">
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Cell label={t("RoadmapPrimitivesLabPage.checkboxCellUnchecked")}>
              <TaskCheckbox
                checked={demoUnchecked}
                onChange={setDemoUnchecked}
                ariaLabel={t("RoadmapPrimitivesLabPage.checkboxCellUnchecked")}
              />
            </Cell>
            <Cell label={t("RoadmapPrimitivesLabPage.checkboxCellChecked")}>
              <TaskCheckbox
                checked={demoChecked}
                onChange={setDemoChecked}
                ariaLabel={t("RoadmapPrimitivesLabPage.checkboxCellChecked")}
              />
            </Cell>
            <Cell label={t("RoadmapPrimitivesLabPage.checkboxCellDisabled")}>
              <TaskCheckbox
                checked={false}
                onChange={() => {}}
                ariaLabel={t("RoadmapPrimitivesLabPage.checkboxAriaDisabledUnchecked")}
                disabled
              />
            </Cell>
            <Cell label={t("RoadmapPrimitivesLabPage.checkboxCellDisabledChecked")}>
              <TaskCheckbox
                checked={true}
                onChange={() => {}}
                ariaLabel={t("RoadmapPrimitivesLabPage.checkboxAriaDisabledChecked")}
                disabled
              />
            </Cell>
          </Stack>
          <Stack spacing={0.5} sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.checkboxNote1")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.checkboxNote2")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.checkboxNote3")}
            </Typography>
          </Stack>
        </Section>

        {/* ── ProductChip ──────────────────────────────────────────────── */}
        <Section title={t("RoadmapPrimitivesLabPage.navProductChip")} anchor="chip">
          <Stack spacing={3}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", display: "block", mb: 1.5 }}>
                {t("RoadmapPrimitivesLabPage.chipInternalHrefNote")}
              </Typography>
              <ProductChip
                productSlug="portfolio-kit"
                label={t("RoadmapPrimitivesLabPage.chipPortfolioKit")}
                href="/portfolio-kit"
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", display: "block", mb: 1.5 }}>
                {t("RoadmapPrimitivesLabPage.chipExternalHrefNote")}
              </Typography>
              <ProductChip
                productSlug="ai-interview-coach"
                label={t("RoadmapPrimitivesLabPage.chipAnthropicDocs")}
                href="https://docs.anthropic.com"
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", display: "block", mb: 1.5 }}>
                {t("RoadmapPrimitivesLabPage.chipPairNote")}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <ProductChip productSlug="portfolio-kit" label={t("RoadmapPrimitivesLabPage.chipPortfolioKit")} href="/portfolio-kit" />
                <ProductChip productSlug="resume-mastery" label={t("RoadmapPrimitivesLabPage.chipResumeMasteryAts")} href="/resume-mastery/ats-score" />
              </Stack>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", display: "block", mb: 1.5 }}>
                {t("RoadmapPrimitivesLabPage.chipAllSlugsNote")}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <ProductChip productSlug="agentic-mastery" label={t("RoadmapPrimitivesLabPage.chipAgenticMastery")} href="/agentic-ai-mastery" />
                <ProductChip productSlug="career-transition" label={t("RoadmapPrimitivesLabPage.chipCareerTransition")} href="/career-transition" />
                <ProductChip productSlug="portfolio-kit" label={t("RoadmapPrimitivesLabPage.chipPortfolioKit")} href="/portfolio-kit" />
                <ProductChip productSlug="resume-mastery" label={t("RoadmapPrimitivesLabPage.chipResumeMastery")} href="/resume-mastery" />
                <ProductChip productSlug="interview-playbook" label={t("RoadmapPrimitivesLabPage.chipInterviewPlaybook")} href="/agentic-interviewing" />
                <ProductChip productSlug="linkedin-presence" label={t("RoadmapPrimitivesLabPage.chipLinkedinPresence")} href="/linkedin-presence" />
                <ProductChip productSlug="ai-interview-coach" label={t("RoadmapPrimitivesLabPage.chipAiInterviewCoach")} href="/coach" />
                <ProductChip productSlug="assessment" label={t("RoadmapPrimitivesLabPage.chipAssessment")} href="/career-transition/assessment" />
              </Stack>
            </Box>
          </Stack>
          <Stack spacing={0.5} sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.chipNote1")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.chipNote2")}
            </Typography>
          </Stack>
        </Section>

        {/* ── StatusDot ────────────────────────────────────────────────── */}
        <Section title={t("RoadmapPrimitivesLabPage.navStatusDot")} anchor="dot">
          <Stack spacing={4}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", display: "block", mb: 1.5 }}>
                {t("RoadmapPrimitivesLabPage.dotSizeSmNote")}
              </Typography>
              <Stack direction="row" spacing={3} alignItems="center">
                <Cell label={t("RoadmapPrimitivesLabPage.dotDoneLabel")}>
                  <StatusDot status="done" size="sm" />
                </Cell>
                <Cell label={t("RoadmapPrimitivesLabPage.dotCurrentLabel")}>
                  <StatusDot status="current" size="sm" />
                </Cell>
                <Cell label={t("RoadmapPrimitivesLabPage.dotUpcomingLabel")}>
                  <StatusDot status="upcoming" size="sm" />
                </Cell>
              </Stack>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", display: "block", mb: 1.5 }}>
                {t("RoadmapPrimitivesLabPage.dotSizeMdNote")}
              </Typography>
              <Stack direction="row" spacing={3} alignItems="center">
                <Cell label={t("RoadmapPrimitivesLabPage.dotDoneLabel")}>
                  <StatusDot status="done" size="md" />
                </Cell>
                <Cell label={t("RoadmapPrimitivesLabPage.dotCurrentLabel")}>
                  <StatusDot status="current" size="md" />
                </Cell>
                <Cell label={t("RoadmapPrimitivesLabPage.dotUpcomingLabel")}>
                  <StatusDot status="upcoming" size="md" />
                </Cell>
              </Stack>
            </Box>
          </Stack>
          <Stack spacing={0.5} sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.dotNote1")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.dotNote2")}
            </Typography>
          </Stack>
        </Section>

        {/* ── ProgressBar ──────────────────────────────────────────────── */}
        <Section title={t("RoadmapPrimitivesLabPage.navProgressBar")} anchor="bar">
          <Stack spacing={4}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", display: "block", mb: 1.5 }}>
                {t("RoadmapPrimitivesLabPage.progressStaticExamplesNote")}
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>0 / 100</Typography>
                  <ProgressBar value={0} ariaLabel={t("RoadmapPrimitivesLabPage.progressAriaDemo0")} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>25 / 100</Typography>
                  <ProgressBar value={25} ariaLabel={t("RoadmapPrimitivesLabPage.progressAriaDemo25")} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>50 / 100</Typography>
                  <ProgressBar value={50} ariaLabel={t("RoadmapPrimitivesLabPage.progressAriaDemo50")} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>100 / 100</Typography>
                  <ProgressBar value={100} ariaLabel={t("RoadmapPrimitivesLabPage.progressAriaDemo100")} />
                </Box>
              </Stack>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", display: "block", mb: 1.5 }}>
                {t("RoadmapPrimitivesLabPage.progressInteractiveNote")}
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                    {progressValue} / 34 ({Math.round((progressValue / 34) * 100)}%)
                  </Typography>
                  <ProgressBar value={progressValue} max={34} ariaLabel={t("RoadmapPrimitivesLabPage.progressAriaRoadmap")} />
                </Box>
                <Stack direction="row" spacing={1}>
                  {[0, 7, 17, 24, 34].map((n) => (
                    <Box
                      key={n}
                      component="button"
                      onClick={() => setProgressValue(n)}
                      sx={{
                        px: 2,
                        py: 0.75,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "background.paper",
                        cursor: "pointer",
                        fontSize: 13,
                        "&:hover": { borderColor: "secondary.main" },
                      }}
                    >
                      {t("RoadmapPrimitivesLabPage.setToButton", { n })}
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Box>
          </Stack>
          <Stack spacing={0.5} sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.progressNote1")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.progressNote2")}
            </Typography>
          </Stack>
        </Section>

        {/* ── TaskCard ─────────────────────────────────────────────────── */}
        <Section title={t("RoadmapPrimitivesLabPage.navTaskCard")} anchor="taskcard">
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace", display: "block", mb: 1.5 }}
              >
                {t("RoadmapPrimitivesLabPage.taskCardFocusNote")}
              </Typography>
              <TaskCard
                task={
                  mergeWeek({ ...weekWithToggleState, tasks: [FIXTURE_TASKS_WEEK_2[0]] }, doneTaskIds)
                    .tasks[0]
                }
                onToggle={handleToggle}
                isFocus
                weekIndex={2}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace", display: "block", mb: 1.5 }}
              >
                {t("RoadmapPrimitivesLabPage.taskCardStandardNote")}
              </Typography>
              <TaskCard
                task={
                  mergeWeek({ ...weekWithToggleState, tasks: [FIXTURE_TASKS_WEEK_2[1]] }, doneTaskIds)
                    .tasks[0]
                }
                onToggle={handleToggle}
                weekIndex={2}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace", display: "block", mb: 1.5 }}
              >
                {t("RoadmapPrimitivesLabPage.taskCardDoneNote")}
              </Typography>
              <TaskCard
                task={
                  mergeWeek({ ...weekWithToggleState, tasks: [FIXTURE_TASKS_WEEK_1[0]] }, doneTaskIds)
                    .tasks[0]
                }
                onToggle={handleToggle}
                weekIndex={1}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace", display: "block", mb: 1.5 }}
              >
                {t("RoadmapPrimitivesLabPage.taskCardBlockedNote")}
              </Typography>
              <TaskCard
                task={
                  mergeWeek({ ...weekWithToggleState, tasks: [FIXTURE_TASKS_WEEK_3[0]] }, doneTaskIds)
                    .tasks[0]
                }
                onToggle={handleToggle}
                weekIndex={3}
              />
            </Box>
          </Stack>
          <Stack spacing={0.5} sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.taskCardNote1")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.taskCardNote2")}
            </Typography>
          </Stack>
        </Section>

        {/* ── WeekRail ─────────────────────────────────────────────────── */}
        <Section title={t("RoadmapPrimitivesLabPage.navWeekRail")} anchor="weekrail">
          <Stack spacing={4}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace", display: "block", mb: 1.5 }}
              >
                {t("RoadmapPrimitivesLabPage.weekRailSingleNote")}
              </Typography>
              <WeekRail
                week={weekWithToggleState}
                isFirstInPhase
                focusTaskId={focusTaskId}
                onToggle={handleToggle}
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace", display: "block", mb: 1.5 }}
              >
                {t("RoadmapPrimitivesLabPage.weekRailCompactNote")}
              </Typography>
              <WeekRail
                week={weekWithToggleState}
                isFirstInPhase
                compact
                focusTaskId={focusTaskId}
                onToggle={handleToggle}
              />
            </Box>
          </Stack>
          <Stack spacing={0.5} sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.weekRailNote1")}
            </Typography>
          </Stack>
        </Section>

        {/* ── PhaseSection ─────────────────────────────────────────────── */}
        <Section title={t("RoadmapPrimitivesLabPage.navPhaseSection")} anchor="phasesection">
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace", display: "block", mb: 3 }}
          >
            {t("RoadmapPrimitivesLabPage.phaseSectionIntroNote")}
          </Typography>
          <Stack spacing={6}>
            {roadmapWithToggleState.phases.map((phase) => (
              <Box key={phase.index}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: "monospace", display: "block", mb: 1.5 }}
                >
                  {t("RoadmapPrimitivesLabPage.phaseCaption", {
                    index: phase.index,
                    status: phase.status,
                    detail: phase.index <= 2
                      ? t("RoadmapPrimitivesLabPage.phaseCaptionFullDetail")
                      : t("RoadmapPrimitivesLabPage.phaseCaptionCompact"),
                  })}
                </Typography>
                <PhaseSection
                  phase={phase}
                  compact={phase.index >= 3}
                  focusTaskId={focusTaskId}
                  onToggle={handleToggle}
                />
              </Box>
            ))}
          </Stack>
          <Stack spacing={0.5} sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.phaseSectionNote1")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.phaseSectionNote2")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.phaseSectionNote3")}
            </Typography>
          </Stack>
        </Section>

        {/* ── RoadmapIntro ─────────────────────────────────────────────── */}
        <Section title={t("RoadmapPrimitivesLabPage.navRoadmapIntro")} anchor="intro">
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <RoadmapIntro roadmap={roadmapWithToggleState} />
          </Box>
          <Stack spacing={0.5} sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.introNote1")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.introNote2")}
            </Typography>
          </Stack>
        </Section>

        {/* ── RoadmapMinimap ──────────────────────────────────────────── */}
        <Section title={t("RoadmapPrimitivesLabPage.navRoadmapMinimap")} anchor="minimap">
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
              maxWidth: 240,
              // Override the component's production-only behaviors so it
              // renders inline within this preview cell at any viewport width:
              //   1. position: sticky → static (no viewport pinning)
              //   2. height: 100vh → auto (no full-screen takeover)
              //   3. display: none below 1100px → block (always visible)
              // Production behavior is correct; this just adapts for review.
              "& > aside": {
                position: "static !important",
                height: "auto !important",
                borderRight: "none !important",
                display: "block !important",
              },
            }}
          >
            <RoadmapMinimap
              roadmap={roadmapWithToggleState}
              progress={progress}
              currentWeekIndex={currentWeek?.index ?? null}
            />
          </Box>
          <Stack spacing={0.5} sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              <Trans
                i18nKey="RoadmapPrimitivesLabPage.minimapNote1"
                t={t}
                components={[<code key="b0" />]}
              />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.minimapNote2")}
            </Typography>
          </Stack>
        </Section>

        {/* ── RoadmapStickyHeader ─────────────────────────────────────── */}
        <Section title={t("RoadmapPrimitivesLabPage.navRoadmapStickyHeader")} anchor="stickyheader">
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
              // Same override pattern — render the sticky header inline so
              // the preview doesn't pin it at viewport top.
              "& > header": {
                position: "static !important",
              },
            }}
          >
            <RoadmapStickyHeader
              currentTask={focusTask}
              currentPhase={currentPhase}
              currentWeek={currentWeek}
              nextMilestone={nextMilestone}
            />
          </Box>
          <Stack spacing={0.5} sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.stickyHeaderNote1")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.stickyHeaderNote2")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("RoadmapPrimitivesLabPage.stickyHeaderNote3")}
            </Typography>
          </Stack>
        </Section>
      </Stack>
    </Container>
  );
}
